import { useState } from 'react'
import { useUsers } from '../../hooks/useUsers'
import type { AppUser } from '../../hooks/useUsers'
import { usePermissionGroups } from '../../hooks/usePermissionGroups'
import { ROLES } from '../../lib/roles'

const roleBadgeClass: Record<string, string> = {
  ADMIN: 'bg-red-100 text-red-800',
  OPERATIONS: 'bg-blue-100 text-blue-800',
  ACCOUNTS: 'bg-green-100 text-green-800',
  SALES: 'bg-orange-100 text-orange-800',
  VIEWER: 'bg-gray-100 text-gray-800',
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const BLANK_USER: AppUser = {
  id: '',
  user_email: '',
  full_name: null,
  role: 'OPERATIONS',
  roles: ['OPERATIONS'],
  department: null,
  branch: 'BIRMINGHAM',
  initials: null,
  is_active: true,
  created_at: '',
  group_id: null,
  boxtop_owner_name: null,
  ecustoms_username: null,
  ecustoms_password: null,
}

export default function Users() {
  const { users, loading, saveUser, toggleActive } = useUsers()
  const { groups } = usePermissionGroups()
  const [roleModal, setRoleModal] = useState<AppUser | null>(null)
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')
  const [userEmailInput, setUserEmailInput] = useState('')
  const [isActiveInput, setIsActiveInput] = useState(true)
  const [boxtopOwnerName, setBoxtopOwnerName] = useState('')
  const [ecustomsUsername, setEcustomsUsername] = useState('')
  const [ecustomsPassword, setEcustomsPassword] = useState('') // new value only — never prefilled with the existing one
  const [clearPassword, setClearPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [showInactive, setShowInactive] = useState(false)

  const visibleUsers = showInactive ? users : users.filter(u => u.is_active)

  const openRoleEdit = (u: AppUser) => {
    setRoleModal(u)
    setSelectedRoles(u.roles?.length ? u.roles : [u.role])
    setSelectedGroupId(u.group_id || '')
    setUserEmailInput(u.user_email)
    setIsActiveInput(u.is_active)
    setBoxtopOwnerName(u.boxtop_owner_name || '')
    setEcustomsUsername(u.ecustoms_username || '')
    setEcustomsPassword('')
    setClearPassword(false)
    setShowPassword(false)
    setSaveError('')
  }

  const openAddUser = () => {
    setRoleModal(BLANK_USER)
    setSelectedRoles(BLANK_USER.roles)
    setSelectedGroupId('')
    setUserEmailInput('')
    setIsActiveInput(true)
    setBoxtopOwnerName('')
    setEcustomsUsername('')
    setEcustomsPassword('')
    setClearPassword(false)
    setShowPassword(false)
    setSaveError('')
  }

  const isNewUser = !!roleModal && !roleModal.id

  const toggleRole = (r: string) => {
    setSelectedRoles(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r])
  }

  const handleRoleSave = async () => {
    if (!roleModal) return
    if (!userEmailInput.trim()) {
      setSaveError('Email is required')
      return
    }
    if (selectedRoles.length === 0) {
      setSaveError('Select at least one role')
      return
    }
    setSaving(true)
    setSaveError('')
    // roles[] is authoritative going forward; the legacy `role` column is
    // kept in sync by a DB trigger, so it's never written from here.
    const payload: Partial<AppUser> = {
      ...roleModal,
      user_email: userEmailInput.trim(),
      // Send roles in fixed ROLES order so the trigger-derived legacy
      // `role` column stays the highest-priority role, not click order.
      roles: ROLES.filter(r => selectedRoles.includes(r)),
      group_id: selectedGroupId || null,
      is_active: isActiveInput,
      boxtop_owner_name: boxtopOwnerName.trim() || null,
      ecustoms_username: ecustomsUsername.trim() || null,
    }
    delete (payload as any).role
    if (isNewUser) delete (payload as any).id
    // Password: blank input leaves the existing value untouched (omit the key entirely);
    // "Clear password" explicitly nulls it; a non-blank input replaces it.
    if (clearPassword) {
      payload.ecustoms_password = null
    } else if (ecustomsPassword.trim()) {
      payload.ecustoms_password = ecustomsPassword.trim()
    }
    const { error } = await saveUser(payload)
    setSaving(false)
    if (error) {
      setSaveError((error as any).code === '23505' ? 'A user with this email already exists.' : error.message)
      return
    }
    setRoleModal(null)
  }

  const groupName = (id: string | null) => {
    if (!id) return null
    return groups.find(g => g.id === id)?.name ?? null
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Manage Users</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">View and manage system user accounts and roles</p>
        </div>
        <button
          onClick={openAddUser}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          + Add User
        </button>
      </div>

      <label className="flex items-center gap-2 mb-3 text-sm text-gray-600 dark:text-gray-400">
        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="rounded" />
        Show inactive ({users.length - users.filter(u => u.is_active).length})
      </label>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading users...</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                {['Email', 'Roles', 'Group', 'BoxTop Owner', 'e-Customs', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {visibleUsers.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-4 py-3 text-gray-800 dark:text-gray-200">{u.user_email}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles?.length ? u.roles : [u.role]).map(r => (
                        <span key={r} className={`px-2 py-1 rounded-full text-xs font-semibold ${roleBadgeClass[r] || 'bg-gray-100 text-gray-700'}`}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                    {groupName(u.group_id) ?? <span className="text-gray-300 dark:text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs">
                    {u.boxtop_owner_name || <span className="text-yellow-600 dark:text-yellow-500">Unmapped</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">{u.ecustoms_username || '—'}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.ecustoms_password ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.ecustoms_password ? 'Password set' : 'No password'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 space-x-3">
                    <button
                      onClick={() => openRoleEdit(u)}
                      className="text-blue-600 hover:underline text-xs font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => toggleActive(u.id, !u.is_active)}
                      className={`text-xs font-medium ${u.is_active ? 'text-red-600 hover:underline' : 'text-green-600 hover:underline'}`}
                    >
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {visibleUsers.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    {users.length === 0 ? 'No users found' : 'No active users — check "Show inactive"'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {roleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm max-h-[90vh] flex flex-col">
            <div className="p-6 pb-0 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">{isNewUser ? 'Add User' : 'Edit User'}</h2>
            </div>

            <div className="space-y-4 px-6 pb-6 overflow-y-auto min-h-0">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={userEmailInput}
                  onChange={e => setUserEmailInput(e.target.value)}
                  placeholder="e.g. name@cyukltd.com"
                  autoComplete="off"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                {!isNewUser && (
                  <p className="text-xs text-gray-400 mt-1">
                    Updates the login identity mapping only (user_roles.user_email) — does not change the account's sign-in email.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Roles <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map(r => (
                    <label key={r} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(r)}
                        onChange={() => toggleRole(r)}
                        className="rounded"
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Permission Group</label>
                <select
                  value={selectedGroupId}
                  onChange={e => setSelectedGroupId(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">— No group —</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">BoxTop Owner Name</label>
                <input
                  type="text"
                  value={boxtopOwnerName}
                  onChange={e => setBoxtopOwnerName(e.target.value)}
                  placeholder="e.g. Gary Liu (matches BoxTop Owner dropdown value)"
                  autoComplete="off"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
                <p className="text-xs text-gray-400 mt-1">Used as BoxTop's Owner field when jobs sync — type the exact name as it appears in BoxTop.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">e-Customs Username</label>
                <input
                  type="text"
                  value={ecustomsUsername}
                  onChange={e => setEcustomsUsername(e.target.value)}
                  placeholder="e.g. gliu"
                  autoComplete="off"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">e-Customs Password</label>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    clearPassword ? 'bg-red-100 text-red-700' : roleModal.ecustoms_password ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {clearPassword ? 'Will clear' : roleModal.ecustoms_password ? 'Set' : 'Not set'}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={ecustomsPassword}
                    onChange={e => { setEcustomsPassword(e.target.value); if (e.target.value) setClearPassword(false) }}
                    placeholder={roleModal.ecustoms_password ? '••••••••• (leave blank to keep current)' : 'Enter password'}
                    autoComplete="new-password"
                    disabled={clearPassword}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>
                {roleModal.ecustoms_password && !clearPassword && (
                  <button
                    type="button"
                    onClick={() => { setClearPassword(true); setEcustomsPassword('') }}
                    className="text-xs text-red-500 hover:underline mt-1"
                  >
                    Clear password
                  </button>
                )}
                {clearPassword && (
                  <button
                    type="button"
                    onClick={() => setClearPassword(false)}
                    className="text-xs text-gray-500 hover:underline mt-1"
                  >
                    Undo clear
                  </button>
                )}
                <p className="text-xs text-gray-400 mt-1">Stored as plain text (matches BoxTop's own practice) — used by a future e-Customs sync to authenticate with Descartes.</p>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={isActiveInput} onChange={e => setIsActiveInput(e.target.checked)} className="rounded" />
                Active
                <span className="text-xs text-gray-400">(inactive users are hidden from pickers but keep their audit history)</span>
              </label>
            </div>

            <div className="flex-shrink-0 p-6 pt-4 border-t border-gray-100 dark:border-gray-700">
              {saveError && (
                <p className="text-xs text-red-600 dark:text-red-400 mb-3">{saveError}</p>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setRoleModal(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRoleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
